import { r as registerInstance, c as createEvent, h, F as Fragment, g as getElement } from './index-e3eeb4ec.js';
import { L as LIVE_UPCOMING_STATUS, E as EVENTS, i as isAtLiveEdge, S as STATUS, K as KEYBOARD_KEY_CODES, I as INDICATORS, V as VOLUME_INCREMENT, a as SKIP_INCREMENT, P as PLAYER_VERSION, b as PhoenixCBCCore, c as EMPTY_MEDIA_DATA } from './index-fd930a73.js';
import { i as isClientSideAdvertising } from './index-f6dc0d1e.js';
import './index-6afae4e4.js';
const MediaStateObserver = publicAPI => {
  var _a, _b;
  const subscribersList = new Set();
  const observableState = {
    isMediaLoading: true,
    isSeekable: (_a = publicAPI.media.canSeek()) !== null && _a !== void 0 ? _a : false,
    currentTime: (_b = publicAPI.media.getCurrentTime()) !== null && _b !== void 0 ? _b : null,
    duration: null,
    seekableRange: {
      start: 0,
      end: 0
    },
    liveSyncPosition: 0,
    isAtLiveEdge: true,
    bufferData: {
      value: 0,
      max: 0
    },
    status: Object.assign({}, publicAPI.getStatus()),
    isClientSideAdvertising: false,
    volume: 0,
    upcomingLiveStatus: LIVE_UPCOMING_STATUS.READY,
    recommendations: null,
    aspectRatio: null
  };
  const notifySubscribers = () => {
    for (const subscriberCallback of subscribersList.values()) {
      subscriberCallback(Object.assign({}, observableState));
    }
  };
  const onUpcomingLiveStatusChange = status => {
    observableState.upcomingLiveStatus = status;
    notifySubscribers();
  };
  const onStatusChange = status => {
    observableState.status.to = status.to;
    observableState.status.from = status.from;
    observableState.status.data = Object.assign({}, status.data);
    observableState.isClientSideAdvertising = isClientSideAdvertising(status);
    notifySubscribers();
  };
  const onBufferUpdated = data => {
    const canSeek = publicAPI.media.canSeek();
    if (canSeek !== observableState.isSeekable) observableState.isSeekable = canSeek;
    if (observableState.isMediaLoading) observableState.isMediaLoading = false;
    const currentTime = publicAPI.media.getCurrentTime();
    const seekable = data.seekable;
    const newBufferData = Object.assign({}, observableState.bufferData);
    if (canSeek) {
      const buffer = data.buffers;
      const max = Math.max(0, seekable.end - seekable.start);
      for (let i = 0; i < (buffer === null || buffer === void 0 ? void 0 : buffer.length); i++) {
        const startPos = buffer.start(i);
        const endPos = buffer.end(i);
        if (currentTime >= startPos && currentTime <= endPos) {
          newBufferData.value = endPos;
          newBufferData.max = max;
          break;
        }
      }
    }
    const liveEdge = Math.max(observableState.liveSyncPosition, seekable.end);
    observableState.isAtLiveEdge = isAtLiveEdge(currentTime, liveEdge);
    observableState.currentTime = currentTime;
    observableState.seekableRange = seekable;
    observableState.bufferData = newBufferData;
    notifySubscribers();
  };
  const onTimeUpdate = data => {
    if (observableState.duration !== data.duration) observableState.duration = data.duration;
    const liveEdge = Math.max(observableState.liveSyncPosition, observableState.seekableRange.end);
    observableState.isAtLiveEdge = isAtLiveEdge(data.currentTime, liveEdge);
    observableState.currentTime = data.currentTime;
    notifySubscribers();
  };
  const onLiveSyncPositionChange = liveSyncPosition => {
    observableState.liveSyncPosition = liveSyncPosition;
    const liveEdge = Math.max(liveSyncPosition, observableState.seekableRange.end);
    observableState.isAtLiveEdge = isAtLiveEdge(observableState.currentTime, liveEdge);
    notifySubscribers();
  };
  const onPlaybackInitialPlay = () => {
    observableState.isMediaLoading = true;
    observableState.duration = null;
    observableState.currentTime = publicAPI.media.getCurrentTime();
    observableState.isSeekable = publicAPI.media.canSeek();
    observableState.volume = publicAPI.media.getVolume();
    observableState.recommendations = null;
    notifySubscribers();
  };
  const onVolumeChange = volume => {
    observableState.volume = volume;
    notifySubscribers();
  };
  const onRecommendationsAvailable = recommendations => {
    observableState.recommendations = recommendations;
    notifySubscribers();
  };
  const onMediaAspectRatioAvailable = aspectRatio => {
    observableState.aspectRatio = aspectRatio;
    notifySubscribers();
  };
  const subscriptionsMap = new Map([[EVENTS.PLAYBACK_INITIALPLAY, onPlaybackInitialPlay], [EVENTS.LIVE_SYNC_POSITION_CHANGE, onLiveSyncPositionChange], [EVENTS.PLAYBACK_TIMEUPDATED, onTimeUpdate], [EVENTS.BUFFER_UPDATED, onBufferUpdated], [EVENTS.STATUS_CHANGE, onStatusChange], [EVENTS.UPCOMING_LIVE_UPDATE, onUpcomingLiveStatusChange], [EVENTS.PLAYBACK_VOLUMECHANGE, onVolumeChange], [EVENTS.RECOMMENDATIONS_AVAILABLE, onRecommendationsAvailable], [EVENTS.MEDIA_ASPECT_RATIO_AVAILABLE, onMediaAspectRatioAvailable]]);
  const init = () => {
    for (const [event, handler] of subscriptionsMap.entries()) {
      publicAPI.on(event, handler);
    }
  };
  const destroy = () => {
    subscribersList.clear();
    for (const [event, handler] of subscriptionsMap.entries()) {
      publicAPI.off(event, handler);
    }
  };
  const subscribe = subscriberCallback => {
    if (subscribersList.has(subscriberCallback)) return;
    subscribersList.add(subscriberCallback);
    subscriberCallback(Object.assign({}, observableState));
  };
  const unsubscribe = subscriberCallback => {
    subscribersList.delete(subscriberCallback);
  };
  init();
  return {
    subscribe,
    unsubscribe,
    destroy
  };
};
const UserInteractionsObserver = () => {
  const subscribersList = new Set();
  const notifySubscribers = payload => {
    for (const subscriberCallback of subscribersList.values()) {
      subscriberCallback(payload);
    }
  };
  const subscribe = subscriberCallback => {
    subscribersList.add(subscriberCallback);
  };
  const unsubscribe = subscriberCallback => {
    subscribersList.delete(subscriberCallback);
  };
  const destroy = () => {
    subscribersList.clear();
  };
  return {
    subscribe,
    unsubscribe,
    notifySubscribers,
    destroy
  };
};
const nodeEventsManager = (_publicAPI, containerNode) => {
  var _a;
  let publicAPI = _publicAPI;
  const eventData = {
    interactionOwner: (_a = containerNode.dataset.ownerName) !== null && _a !== void 0 ? _a : containerNode.tagName,
    interactionType: "",
    interactionName: ""
  };
  const shouldIgnore = event => {
    var _a, _b;
    if (((_a = publicAPI.mediaData) === null || _a === void 0 ? void 0 : _a.format) !== "video") return true;
    if (event.target["tagName"] !== "DIV") return true;
    if (isUpcomingLive()) return true;
    if ((_b = event.target.classList) === null || _b === void 0 ? void 0 : _b.contains("phx-error-overlay")) return true;
    return false;
  };
  const isUpcomingLive = () => {
    var _a, _b;
    return publicAPI.getStatus().to !== STATUS.PLAYING && ((_a = publicAPI.mediaData) === null || _a === void 0 ? void 0 : _a.isLive) && ((_b = publicAPI.mediaData) === null || _b === void 0 ? void 0 : _b.pubDate) > Date.now();
  };
  const handlePlaybackInput = event => {
    var _a;
    if (shouldIgnore(event)) return;
    event.preventDefault();
    eventData.interactionName = "";
    eventData.interactionType = (_a = event.constructor.name) !== null && _a !== void 0 ? _a : "";
    const isSeekable = publicAPI.media.canSeek();
    const playerStatus = publicAPI.getStatus().to;
    switch (playerStatus) {
      case STATUS.PLAYING:
      case STATUS.ADVERTISING:
        if (isSeekable) {
          publicAPI.media.pause();
          eventData.interactionName = INDICATORS.PAUSE;
        } else {
          publicAPI.media.stop();
          eventData.interactionName = INDICATORS.STOP;
        }
        break;
      case STATUS.ENDED:
      case STATUS.PAUSED:
      case STATUS.ADVERTISING_PAUSED:
        publicAPI.media.play();
        eventData.interactionName = INDICATORS.PLAY;
        break;
    }
    if (eventData.interactionName) {
      publicAPI.userInteractionsObserver.notifySubscribers(Object.assign({}, eventData));
    }
  };
  const handleArrowVolume = event => {
    var _a;
    if (event.target["tagName"] !== "DIV") return;
    event.preventDefault();
    eventData.interactionType = (_a = event.constructor.name) !== null && _a !== void 0 ? _a : "";
    const amt = event.code === KEYBOARD_KEY_CODES.ARROWUP ? VOLUME_INCREMENT : -VOLUME_INCREMENT;
    const newVolume = Math.round((publicAPI.media.getVolume() + amt) * 10) / 10;
    if (newVolume > 1 || newVolume < 0) return;
    eventData.interactionName = amt > 0 ? INDICATORS.VOLUME_UP : INDICATORS.VOLUME_DOWN;
    publicAPI.media.setVolume(newVolume);
    publicAPI.userInteractionsObserver.notifySubscribers(Object.assign({}, eventData));
  };
  const handleArrowSeek = event => {
    var _a;
    const playerStatus = publicAPI.getStatus().to;
    if (event.target["tagName"] !== "DIV" || playerStatus === STATUS.ADVERTISING || playerStatus === STATUS.ADVERTISING_PAUSED) return;
    event.preventDefault();
    eventData.interactionType = (_a = event.constructor.name) !== null && _a !== void 0 ? _a : "";
    const amt = event.code === KEYBOARD_KEY_CODES.ARROWLEFT ? -SKIP_INCREMENT : SKIP_INCREMENT;
    let newTime = publicAPI.media.getCurrentTime() + amt;
    if (newTime < 0) newTime = 0;
    eventData.interactionName = amt > 0 ? INDICATORS.SKIP_FORWARD : INDICATORS.SKIP_BACKWARD;
    publicAPI.media.setCurrentTime(newTime);
    publicAPI.userInteractionsObserver.notifySubscribers(Object.assign({}, eventData));
  };
  const handleToggleMute = event => {
    var _a, _b, _c;
    const mutedStatus = (_a = publicAPI.media) === null || _a === void 0 ? void 0 : _a.getMuted();
    eventData.interactionName = mutedStatus ? INDICATORS.VOLUME_UP : INDICATORS.MUTE;
    eventData.interactionType = (_b = event.constructor.name) !== null && _b !== void 0 ? _b : "";
    (_c = publicAPI.media) === null || _c === void 0 ? void 0 : _c.setMuted(!mutedStatus);
    publicAPI.userInteractionsObserver.notifySubscribers(Object.assign({}, eventData));
  };
  const handleToggleFullscreen = event => {
    var _a;
    eventData.interactionType = (_a = event.constructor.name) !== null && _a !== void 0 ? _a : "";
    eventData.interactionName = "toggle-fullscreen";
    publicAPI.userInteractionsObserver.notifySubscribers(Object.assign({}, eventData));
  };
  const handleToggleCaptions = event => {
    var _a;
    eventData.interactionType = (_a = event.constructor.name) !== null && _a !== void 0 ? _a : "";
    eventData.interactionName = "toggle-captions";
    publicAPI.userInteractionsObserver.notifySubscribers(Object.assign({}, eventData));
  };
  const handleKeyDown = event => {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.isComposing) {
      return;
    }
    if (isUpcomingLive()) return;
    switch (event.code) {
      case KEYBOARD_KEY_CODES.SPACE:
        handlePlaybackInput(event);
        break;
      case KEYBOARD_KEY_CODES.ARROWUP:
      case KEYBOARD_KEY_CODES.ARROWDOWN:
        handleArrowVolume(event);
        break;
      case KEYBOARD_KEY_CODES.ARROWLEFT:
      case KEYBOARD_KEY_CODES.ARROWRIGHT:
        handleArrowSeek(event);
        break;
      case KEYBOARD_KEY_CODES.KEYM:
        handleToggleMute(event);
        break;
      case KEYBOARD_KEY_CODES.KEYF:
        handleToggleFullscreen(event);
        break;
      case KEYBOARD_KEY_CODES.KEYC:
        handleToggleCaptions(event);
        break;
    }
  };
  const init = () => {
    containerNode.addEventListener("keydown", handleKeyDown);
    containerNode.addEventListener("click", handlePlaybackInput);
  };
  const destroy = () => {
    containerNode.removeEventListener("keydown", handleKeyDown);
    containerNode.removeEventListener("click", handlePlaybackInput);
  };
  const setPublicAPI = _publicAPI => {
    publicAPI = _publicAPI;
  };
  init();
  return {
    setPublicAPI,
    destroy
  };
};
const DEFAULT_VIDEO_UI_ASPECT_RATIO = 1.778; // 16/9
const PHOENIX_PLAYER_BREAKPOINTS = {
  SMALL: 344,
  MEDIUM: 575,
  LARGE: 720,
  EXTRA_LARGE: 960
};
//Notes:
// Calculating a dimension based on aspect ratio (AR) of width to height (eg 16/9) given in a number (eg 1.7777);
// w = h x AR
// h = w / AR
const calculateSize = config => {
  //config width is prioritized first
  if (config.width && !Number.isNaN(config.width)) {
    return {
      width: config.width,
      height: calculateHeightByConfig(config)
    };
  }
  //config height is prioritized second
  if (config.height && !Number.isNaN(config.height)) {
    return {
      width: calculateWidthByConfig(config),
      height: config.height
    };
  }
  //if we're sizing based on aspect ratio, supplied or based on the media, or the container has no height
  //note: any aspect ratio sizing ignores the container height, and is purely calculated on the container width
  if (config.maxHeight || config.aspectRatio || config.useMediaAspectRatio || !config.containerHeight) {
    return {
      width: config.containerWidth,
      height: calculateHeightWithNoConstraints(config)
    };
  }
  // finally, if we have no sizing restrictions, size by the containers width and height
  return {
    width: config.containerWidth,
    height: config.containerHeight
  };
};
const determineAspectRatioToUse = config => {
  let aspectRatio = DEFAULT_VIDEO_UI_ASPECT_RATIO;
  if (config.aspectRatio && !Number.isNaN(config.aspectRatio)) aspectRatio = config.aspectRatio;
  if (config.useMediaAspectRatio && config.mediaAspectRatio && !Number.isNaN(config.mediaAspectRatio)) aspectRatio = config.mediaAspectRatio;
  return aspectRatio;
};
const calculateHeightByConfig = config => {
  //When calculating height given a width
  //config height is prioritized first
  if (config.height && !Number.isNaN(config.height)) {
    return config.height;
  }
  //finally we will return sizing based on an aspect ratio if nothing else
  const aspectRatio = determineAspectRatioToUse(config);
  return Math.round(config.width / aspectRatio);
};
const calculateWidthByConfig = config => {
  //When calculating width by config, there is no need to check for width,
  //since this function is not called if width is supplied
  // if (config.width && !Number.isNaN(config.width)) {
  //     return config.width;
  // }
  //we will return sizing based on an aspect ratio if nothing else
  const aspectRatio = determineAspectRatioToUse(config);
  return Math.round(config.height * aspectRatio);
};
// Calculate the height of the player when no config.width and config.height are supplied
// Since we do not have config.width and config.height, we can check if the player should be sized to the maxHeight
const calculateHeightWithNoConstraints = config => {
  const aspectRatio = determineAspectRatioToUse(config);
  let calculatedHeight = Math.round(config.containerWidth / aspectRatio);
  if (config.maxHeight && !Number.isNaN(config.maxHeight) && calculatedHeight > config.maxHeight) {
    calculatedHeight = config.maxHeight;
  }
  return calculatedHeight;
};
const getElementInternalSize = element => {
  const cs = getComputedStyle(element);
  const elementWidth = element.offsetWidth;
  const elementHeight = element.offsetHeight;
  const paddingX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
  const paddingY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
  const borderX = parseFloat(cs.borderLeftWidth) + parseFloat(cs.borderRightWidth);
  const borderY = parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
  return {
    width: elementWidth - paddingX - borderX,
    height: elementHeight - paddingY - borderY
  };
};
const indexCss = ".phoenix-player{position:relative;display:block;font-family:sans-serif;width:100%;height:100%}.phoenix-player.phoenix-video{background-color:rgb(40, 40, 40);container-type:size;container-name:phoenix-player}.phoenix-player.phoenix-audio .phoenix-core-container{display:none}.phoenix-player.phoenix-audio.advertising .phoenix-core-container,.phoenix-player.phoenix-audio.advertising .phoenix-ad-ui{position:fixed !important;top:auto !important;right:20px !important;bottom:20px !important;left:auto !important;width:640px !important;height:360px !important;overflow:hidden}.phoenix-player.phoenix-audio.advertising .phoenix-core-container{display:block;box-sizing:border-box;border:1px solid black}.phoenix-player.phoenix-audio.advertising .phoenix-ad-ui{pointer-events:none}@media (pointer: coarse), (hover: none){.phoenix-player label.phx-tooltip{position:absolute;width:1px;height:1px;margin:-1px;padding:0 !important;border:0;overflow:hidden;clip:rect(0, 0, 0, 0);white-space:nowrap;opacity:0 !important}}";
const PhoenixPlayerStyle0 = indexCss;
const PhoenixPlayer = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.statusChanged = createEvent(this, "statusChanged", 7);
    this.playerInitialized = createEvent(this, "playerInitialized", 7);
    this.version = PLAYER_VERSION;
    this.isInitialized = false;
    this.isAdvertising = false;
    this.wasDisconnected = false;
    this.onStatusChange = status => {
      this.playerStatus = status.to;
      this.statusChanged.emit(status);
    };
    this.onObserverUpdate = state => {
      var _a, _b;
      if (this.playerStatus !== state.status.to) {
        this.onStatusChange(state.status);
        this.isAdvertising = (_b = (_a = state.status) === null || _a === void 0 ? void 0 : _a.to) === null || _b === void 0 ? void 0 : _b.startsWith("ADVER");
      }
      if (this.mediaAspectRatio !== state.aspectRatio) {
        this.mediaAspectRatio = state.aspectRatio;
        this.setSize();
      }
    };
    this.initializePlayer = async () => {
      var _a, _b;
      try {
        const PhoenixInstance = PhoenixCBCCore(this.settings);
        const playerPublicAPI = await PhoenixInstance.createPlayer(Object.assign(Object.assign({}, this.config), {
          containerNode: this.containerNode
        }), this.mediaData);
        const mediaStateObserver = MediaStateObserver(playerPublicAPI);
        const userInteractionsObserver = UserInteractionsObserver();
        this.publicAPI = Object.assign(Object.assign({}, playerPublicAPI), {
          mediaData: this.mediaData,
          mediaStateObserver,
          userInteractionsObserver,
          controlsConfig: {
            hideFullscreenBtn: false
          }
        });
        this.publicAPI.mediaStateObserver.subscribe(this.onObserverUpdate);
        this.eventsManager = nodeEventsManager(this.publicAPI, this.containerNode);
        if (!this.playerStatus) {
          this.playerStatus = (_a = this.publicAPI) === null || _a === void 0 ? void 0 : _a.getStatus().to;
        }
        this.isInitialized = true;
        if ((_b = this.config) === null || _b === void 0 ? void 0 : _b.debug) {
          console.log(`[Phoenix Player] PhoenixPlayer -> Version: ${this.version}`);
        }
      } catch (e) {
        console.error(`Unable to createPlayer (v${this.version}): `, e);
      }
    };
    this.onContainerResize = entries => {
      var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
      this.containerWidth = ((_c = (_b = (_a = entries === null || entries === void 0 ? void 0 : entries[0]) === null || _a === void 0 ? void 0 : _a.contentBoxSize) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.inlineSize) || ((_e = (_d = entries === null || entries === void 0 ? void 0 : entries[0]) === null || _d === void 0 ? void 0 : _d.contentRect) === null || _e === void 0 ? void 0 : _e.width) || 0;
      this.containerHeight = ((_h = (_g = (_f = entries === null || entries === void 0 ? void 0 : entries[0]) === null || _f === void 0 ? void 0 : _f.contentBoxSize) === null || _g === void 0 ? void 0 : _g[0]) === null || _h === void 0 ? void 0 : _h.blockSize) || ((_k = (_j = entries === null || entries === void 0 ? void 0 : entries[0]) === null || _j === void 0 ? void 0 : _j.contentRect) === null || _k === void 0 ? void 0 : _k.height) || 0;
      this.setSize();
    };
    this.resizeObserver = "ResizeObserver" in window ? new ResizeObserver(this.onContainerResize) : null;
    this.getStyle = () => {
      var _a;
      const style = {};
      if (this.width) style.width = `${Math.round(this.width)}px`;
      if (this.height && ((_a = this.mediaData) === null || _a === void 0 ? void 0 : _a.format) !== "audio") style.height = `${Math.round(this.height)}px`;
      return style;
    };
    this.getSizeClass = () => {
      if (this.width < PHOENIX_PLAYER_BREAKPOINTS.SMALL) {
        return "phx-extra-small";
      } else if (this.width >= PHOENIX_PLAYER_BREAKPOINTS.SMALL && this.width < PHOENIX_PLAYER_BREAKPOINTS.MEDIUM) {
        return "phx-small";
      } else if (this.width >= PHOENIX_PLAYER_BREAKPOINTS.MEDIUM && this.width < PHOENIX_PLAYER_BREAKPOINTS.LARGE) {
        return "phx-medium";
      } else if (this.width >= PHOENIX_PLAYER_BREAKPOINTS.LARGE && this.width < PHOENIX_PLAYER_BREAKPOINTS.EXTRA_LARGE) {
        return "phx-large";
      } else if (this.width >= PHOENIX_PLAYER_BREAKPOINTS.EXTRA_LARGE) {
        return "phx-extra-large";
      } else {
        return "";
      }
    };
    this.renderAdOverlay = () => {
      if (!this.isAdvertising) return;
      return h("div", {
        class: "phoenix-ad-ui"
      }, h("ad-ui-overlay", {
        publicAPI: this.publicAPI
      }));
    };
    this.renderUI = () => {
      if (!this.isInitialized) return null;
      return h(Fragment, null, h("video-ui", {
        publicAPI: this.publicAPI,
        containerNode: this.containerNode
      }), h("audio-ui", {
        publicAPI: this.publicAPI
      }));
    };
  }
  async play() {
    var _a, _b, _c;
    (_c = (_b = (_a = this.publicAPI) === null || _a === void 0 ? void 0 : _a.media) === null || _b === void 0 ? void 0 : _b.play) === null || _c === void 0 ? void 0 : _c.call(_b);
  }
  async load() {
    var _a, _b, _c, _d, _e, _f;
    (_c = (_b = (_a = this.publicAPI) === null || _a === void 0 ? void 0 : _a.media) === null || _b === void 0 ? void 0 : _b.load) === null || _c === void 0 ? void 0 : _c.call(_b);
    this.publicAPI = Object.assign(Object.assign({}, this.publicAPI), {
      mediaData: EMPTY_MEDIA_DATA
    });
    (_e = (_d = this.resizeObserver) === null || _d === void 0 ? void 0 : _d.unobserve) === null || _e === void 0 ? void 0 : _e.call(_d, (_f = this.phoenixPlayerNode) === null || _f === void 0 ? void 0 : _f.parentElement);
  }
  async unload() {
    var _a, _b, _c;
    (_c = (_b = (_a = this.publicAPI) === null || _a === void 0 ? void 0 : _a.media) === null || _b === void 0 ? void 0 : _b.unload) === null || _c === void 0 ? void 0 : _c.call(_b);
    this.publicAPI = Object.assign(Object.assign({}, this.publicAPI), {
      mediaData: EMPTY_MEDIA_DATA
    });
    this.eventsManager.setPublicAPI(this.publicAPI);
  }
  async stop() {
    var _a, _b, _c;
    (_c = (_b = (_a = this.publicAPI) === null || _a === void 0 ? void 0 : _a.media) === null || _b === void 0 ? void 0 : _b.stop) === null || _c === void 0 ? void 0 : _c.call(_b);
  }
  async setAdsInactive(adsInactive) {
    var _a, _b, _c;
    (_c = (_b = (_a = this.publicAPI) === null || _a === void 0 ? void 0 : _a.media) === null || _b === void 0 ? void 0 : _b.setImaSdkInactive) === null || _c === void 0 ? void 0 : _c.call(_b, adsInactive);
  }
  async updateSizeConfig(sizeConfig) {
    const {
      useMediaAspectRatio = this.sizeConfig.useMediaAspectRatio,
      aspectRatio = this.sizeConfig.aspectRatio,
      width = this.sizeConfig.width,
      height = this.sizeConfig.height,
      maxHeight = this.sizeConfig.maxHeight
    } = sizeConfig;
    this.sizeConfig = Object.assign(Object.assign({}, this.sizeConfig), {
      useMediaAspectRatio,
      aspectRatio,
      width,
      height,
      maxHeight
    });
    this.setSize();
  }
  async updateControlsConfig(controlsConfig) {
    const {
      hideFullscreenBtn
    } = controlsConfig || {};
    this.publicAPI.controlsConfig.hideFullscreenBtn = hideFullscreenBtn !== null && hideFullscreenBtn !== void 0 ? hideFullscreenBtn : false;
  }
  async destroy() {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    (_a = this.eventsManager) === null || _a === void 0 ? void 0 : _a.destroy();
    (_c = (_b = this.publicAPI) === null || _b === void 0 ? void 0 : _b.mediaStateObserver) === null || _c === void 0 ? void 0 : _c.unsubscribe(this.onObserverUpdate);
    (_e = (_d = this.publicAPI) === null || _d === void 0 ? void 0 : _d.mediaStateObserver) === null || _e === void 0 ? void 0 : _e.destroy();
    (_g = (_f = this.publicAPI) === null || _f === void 0 ? void 0 : _f.userInteractionsObserver) === null || _g === void 0 ? void 0 : _g.destroy();
    (_h = this.publicAPI) === null || _h === void 0 ? void 0 : _h.destroy();
  }
  async componentDidLoad() {
    await this.initializePlayer();
  }
  componentWillRender() {
    this.setSize();
  }
  componentDidRender() {
    var _a;
    if (this.isInitialized === true) return;
    if (this.shouldObserveResize()) {
      (_a = this.resizeObserver) === null || _a === void 0 ? void 0 : _a.observe(this.phoenixPlayerNode.parentElement);
    }
  }
  shouldObserveResize() {
    var _a, _b;
    //we don't need to observe the container resizing if have a fixed height or width
    return !(((_a = this.sizeConfig) === null || _a === void 0 ? void 0 : _a.width) || ((_b = this.sizeConfig) === null || _b === void 0 ? void 0 : _b.height));
  }
  setVersion() {
    this.version = PLAYER_VERSION;
  }
  connectedCallback() {
    var _a;
    this.setVersion();
    const {
      useMediaAspectRatio = null,
      aspectRatio = null,
      width = null,
      height = null,
      maxHeight = null
    } = this.config || {};
    this.sizeConfig = {
      useMediaAspectRatio,
      aspectRatio,
      width,
      height,
      maxHeight
    };
    if (this.wasDisconnected) {
      this.wasDisconnected = false;
      (_a = this.resizeObserver) === null || _a === void 0 ? void 0 : _a.observe(this.phoenixPlayerNode.parentElement);
    }
    const parentInternalSize = getElementInternalSize(this.phoenixPlayerNode.parentElement);
    this.containerHeight = parentInternalSize.height;
    this.containerWidth = parentInternalSize.width;
  }
  disconnectedCallback() {
    var _a, _b;
    this.wasDisconnected = true;
    (_a = this.resizeObserver) === null || _a === void 0 ? void 0 : _a.unobserve(this.phoenixPlayerNode.parentElement);
    (_b = this.resizeObserver) === null || _b === void 0 ? void 0 : _b.disconnect();
  }
  setSize() {
    const config = {
      width: this.sizeConfig.width,
      height: this.sizeConfig.height,
      aspectRatio: this.sizeConfig.aspectRatio,
      useMediaAspectRatio: this.sizeConfig.useMediaAspectRatio,
      containerWidth: this.containerWidth,
      containerHeight: this.containerHeight,
      mediaAspectRatio: this.mediaAspectRatio,
      maxHeight: this.sizeConfig.maxHeight
    };
    const size = calculateSize(config);
    if (size.height !== this.height) this.height = size.height;
    if (size.width !== this.width) this.width = size.width;
  }
  WatchMediaDataChange(mediaData) {
    var _a, _b;
    if (!mediaData) throw new Error("mediaData null or undefined");
    this.publicAPI = Object.assign(Object.assign({}, this.publicAPI), {
      mediaData: mediaData
    });
    this.eventsManager.setPublicAPI(this.publicAPI);
    this.publicAPI.media.play(mediaData);
    if (this.shouldObserveResize()) {
      (_a = this.resizeObserver) === null || _a === void 0 ? void 0 : _a.observe(this.phoenixPlayerNode.parentElement);
    } else {
      (_b = this.resizeObserver) === null || _b === void 0 ? void 0 : _b.unobserve(this.phoenixPlayerNode.parentElement);
    }
  }
  WatchIsInitializedChange(isInitialized) {
    this.playerInitialized.emit(isInitialized);
  }
  WatchVersionChange(newVersion) {
    if (newVersion !== PLAYER_VERSION) {
      this.setVersion();
      throw new Error("Property 'version' is read-only!");
    }
  }
  render() {
    var _a, _b;
    const format = ((_a = this.mediaData) === null || _a === void 0 ? void 0 : _a.format) ? `phoenix-${(_b = this.mediaData) === null || _b === void 0 ? void 0 : _b.format}` : "";
    const advertising = this.isAdvertising ? "advertising" : "";
    return h("div", {
      key: '366ce58794609be2bb39ff59fdf5ef05be370172',
      class: `phoenix-player ${format} ${this.getSizeClass()} ${advertising}`,
      style: this.getStyle(),
      "data-owner-name": "phoenix-player-main-node",
      ref: el => this.containerNode = el,
      tabindex: "0"
    }, this.renderUI(), this.renderAdOverlay());
  }
  get phoenixPlayerNode() {
    return getElement(this);
  }
  static get watchers() {
    return {
      "mediaData": ["WatchMediaDataChange"],
      "isInitialized": ["WatchIsInitializedChange"],
      "version": ["WatchVersionChange"]
    };
  }
};
PhoenixPlayer.style = PhoenixPlayerStyle0;
export { PhoenixPlayer as phoenix_player };